import { useState, type ReactNode } from 'react';

import { useT } from '../../i18n';
import { uebergibBelege } from '../../platform/beleguebergabe';
import styles from './Sheets.module.css';

/**
 * Die Belege aus dem Browser herausholen.
 *
 * ⚠️ **Nur im Browser, und der Aufrufer entscheidet das** (`SettingsDialog.tsx`). Am iPad
 * liegen dieselben Dateien in Documents und gehen über Finder oder iTunes hinaus; ein zweiter
 * Weg daneben wäre eine zweite Wahrheit über denselben Bestand. Im Browser dagegen sind sie in
 * IndexedDB eingeschlossen — ohne diesen Knopf käme niemand an sie heran.
 *
 * ⚠️ **Kein Fortschrittsbalken, sondern eine Zahl am Ende.** Wie viele Dateien es werden,
 * weiss erst das Ende des Durchgangs; ein Balken müsste vorher zählen und liefe zweimal über
 * denselben Bestand.
 */
export function BelegeGruppe({
  Group,
}: {
  Group: (p: { label: string; children: ReactNode }) => ReactNode;
}) {
  const t = useT();
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<{ gut: boolean; text: string } | null>(null);

  const hole = async () => {
    setLaeuft(true);
    setMeldung(null);
    try {
      const { dateien, bytes } = await uebergibBelege();
      setMeldung(
        dateien === 0
          ? { gut: true, text: t.belege.leer }
          : { gut: true, text: t.belege.fertig(dateien, Math.round(bytes / 1024 / 1024)) },
      );
    } catch (fehler) {
      setMeldung({
        gut: false,
        text: t.belege.fehler + ' ' + (fehler instanceof Error ? fehler.message : String(fehler)),
      });
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <>
      {Group({
        label: t.belege.group,
        children: (
          <>
            <div className={styles.serverActions}>
              <button
                type="button"
                className={`tap ${styles.stufe}`}
                disabled={laeuft}
                onClick={() => void hole()}
              >
                {laeuft ? t.belege.laeuft : t.belege.holen}
              </button>
            </div>
            <div className={styles.textNote}>{t.belege.hinweis}</div>
            {meldung && (
              <div
                className={styles.textNote}
                style={{ color: meldung.gut ? 'var(--ok, #0a7d4b)' : 'var(--warn, #b4540a)' }}
              >
                {meldung.text}
              </div>
            )}
          </>
        ),
      })}
    </>
  );
}
